/**
 * Analytics — a thin, optional wrapper over PostHog. It exists primarily to make
 * the paywall A/B (see `features/billing/lib/paywallVariants`) measurable:
 * `paywall_viewed` and `paywall_upgrade_clicked` are captured with the assigned
 * variant so a conversion funnel can be read per treatment. The completed
 * purchase is attributed separately via Polar checkout metadata.
 *
 * With no `POSTHOG_KEY` configured (local dev by default) every call is a no-op,
 * so features can `track(...)` unconditionally without guarding on config.
 */
import posthog from "posthog-js";
import { env } from "@/shared/config/env";

let ready = false;

/** Initialise PostHog once, if a key is present. Called from the app entry. */
export function initAnalytics(): void {
  if (ready || !env.POSTHOG_KEY || typeof window === "undefined") return;
  posthog.init(env.POSTHOG_KEY, {
    api_host: env.POSTHOG_HOST,
    capture_pageview: true,
    autocapture: true,
    // Keep it light and privacy-conscious: no session replay, and profiles only
    // for users we explicitly identify.
    disable_session_recording: true,
    person_profiles: "identified_only",
  });
  ready = true;
}

/** Capture an event. No-op until `initAnalytics` has run with a real key. */
export function track(event: string, properties?: Record<string, unknown>): void {
  if (!ready) return;
  posthog.capture(event, properties);
}
